import { AssessmentComponent } from "@pandas/assessment-engine";
import type { Location, Partner, Session } from "@pandas/http-client";
import { StateCreator } from "zustand";
import { FlowType } from "@/lib/flow-logic/flowTypeHelpers";
import { AllScreens } from "@/lib/flow-logic/steps";
import { Storage } from "../index";

export interface LatLong {
	lat: number;
	lng: number;
}

export type AssessmentAttempt = {
	assessmentKey: AssessmentComponent;
	attempts: number;
};

// INITIAL STATE
export type SessionInfoSlice = {
	sessionId: string;
	isAppInitializing: boolean;
	hasSentFinalOfferEmail: boolean;
	initializedScreens: Array<AllScreens>;
	locations: Array<Location>;
	flow: FlowType;
	partnerInfo: Partner | null;
	error: string | unknown | null;
	sessionDetails: Session | null;
	userLocation: LatLong | null;
	assessmentAttempts: Array<AssessmentAttempt>;
	setSessionId: (sessionId: string) => void;
	setInitializedScreens: (initializedScreens: Array<AllScreens>) => void;
	setIsAppInitializing: (isAppInitializing: boolean) => void;
	setHasSentFinalOfferEmail: (hasSentFinalOfferEmail: boolean) => void;
	setError: (error: string | unknown | null) => void;
	setLocations: (locations: Array<Location>) => void;
	setFlow: (flow: FlowType) => void;
	setPartnerInfo: (partnerInfo: Partner) => void;
	setSessionDetails: (sessionDetails: Session | null) => void;
	setUserLocation: (userLocation: LatLong | null) => void;
	setAssessmentAttempts: (attempts: Array<AssessmentAttempt>) => void;
	incrementAttempt: (assessmentKey: AssessmentComponent) => void;
};

const initialState = {
	sessionId: "",
	locations: [],
	flow: FlowType.default,
	partnerInfo: null,
	error: null,
	sessionDetails: null,
	userLocation: null,
	isAppInitializing: true,
	hasSentFinalOfferEmail: false,
	initializedScreens: [],
	assessmentAttempts: [],
};

export const createSessionInfoSlice: StateCreator<
	Storage,
	[],
	[
		[
			"zustand/persist",
			{
				sessionId: string;
				locations: Array<Location>;
				flow: FlowType;
				partnerInfo: Partner | null;
				error: string | unknown | null;
				isAppInitializing: boolean;
				hasSentFinalOfferEmail: boolean;
				initializedScreens: Array<AllScreens>;
				assessmentAttempts: Array<AssessmentAttempt>;
			},
		],
	],
	SessionInfoSlice
> = (set) => ({
	...initialState,
	setSessionId: (sessionId: string) => set(() => ({ sessionId: sessionId })),
	setInitializedScreens: (initializedScreens: Array<AllScreens>) =>
		set(() => ({ initializedScreens: initializedScreens })),
	setIsAppInitializing: (isAppInitializing: boolean) => set(() => ({ isAppInitializing: isAppInitializing })),
	setHasSentFinalOfferEmail: (hasSentFinalOfferEmail: boolean) =>
		set(() => ({ hasSentFinalOfferEmail: hasSentFinalOfferEmail })),
	setError: (error: string | unknown | null) => set(() => ({ error: error })),
	setPartnerInfo: (partnerInfo: Partner) => set(() => ({ partnerInfo: partnerInfo })),
	setLocations: (locations: Array<Location>) => set(() => ({ locations: locations })),
	setFlow: (flow: FlowType) => set(() => ({ flow: flow })),
	setSessionDetails: (sessionDetails: Session | null) => set(() => ({ sessionDetails: sessionDetails })),
	setUserLocation: (userLocation: LatLong | null) => set(() => ({ userLocation: userLocation })),
	setAssessmentAttempts: (attempts: Array<AssessmentAttempt>) =>
		set(() => ({
			assessmentAttempts: attempts,
		})),
	incrementAttempt: (assessmentKey: AssessmentComponent) =>
		set((state) => {
			const existingAttempt = state.assessmentAttempts.find((a) => a.assessmentKey === assessmentKey);
			if (existingAttempt) existingAttempt.attempts += 1;
			else state.assessmentAttempts.push({ assessmentKey, attempts: 1 });

			return {
				assessmentAttempts: [...state.assessmentAttempts],
			};
		}),
});
